import { ActivatedRoute } from '@angular/router';
import {
  AfterViewInit, Directive, ElementRef, NgZone, OnDestroy, inject,
} from '@angular/core';
import { Subscription } from 'rxjs';

import { MULTI_FIELD_SEARCH_KEY } from '@common/search-filter.constants';

const HIGHLIGHT_CLASS = 'search-highlight';
const EXCLUDED_ELEMENTS = [
  '.show-skeleton',
  '[hidden]',
  '[aria-hidden="true"]',
  'ion-icon',
  'script',
  'style',
];
const DETAIL_CONTROLS = [
  'button',
  'ion-button',
  'ion-item[button]',
  'ion-segment-button',
  'ion-checkbox',
  'ion-radio',
  'ion-select',
  'ion-input',
  'ion-textarea',
  'input',
  'select',
  'textarea',
  '[role="button"]',
];

interface Match {
  start: number;
  end: number;
}

interface MarkedText {
  original: Text;
  inserted: Node[];
}

@Directive({
  selector: 'ion-content.nakos-list-content, ion-content.detail-content, ion-list.search-list',
  standalone: false,
})
export class SearchHighlightDirective implements AfterViewInit, OnDestroy {
  private host = inject(ElementRef<HTMLElement>);
  private route = inject(ActivatedRoute);
  private zone = inject(NgZone);
  private querySubscription?: Subscription;
  private observer?: MutationObserver;
  private scheduled = false;
  private destroyed = false;
  private searchTerm = '';
  private marked: MarkedText[] = [];
  private changedText = new Set<Text>();

  private static isVisible(node: Text, excludedSelector: string): boolean {
    let element = node.parentElement;

    while (element) {
      if (element.matches(excludedSelector)) {
        return false;
      }

      const style = getComputedStyle(element);

      if (style.display === 'none' || style.visibility === 'hidden') {
        return false;
      }

      element = element.parentElement;
    }

    return true;
  }

  private static findMatches(content: string, terms: string[]): Match[] {
    const matches: Match[] = [];

    for (const term of terms) {
      const escaped = term.replace(/[.*+?^${}()|[\]\\]/gu, '\\$&');
      const expression = new RegExp(escaped, 'giu');
      let result = expression.exec(content);

      while (result) {
        matches.push({
          end: result.index + result[0].length,
          start: result.index,
        });
        result = expression.exec(content);
      }
    }

    matches.sort((first, second) => { return first.start - second.start || second.end - first.end; });

    const nonOverlapping: Match[] = [];

    for (const match of matches) {
      if (!nonOverlapping.length || match.start >= nonOverlapping[nonOverlapping.length - 1].end) {
        nonOverlapping.push(match);
      }
    }

    return nonOverlapping;
  }

  private static getHighlightTerms(searchTerm: string, root: HTMLElement): string[] {
    const words = searchTerm.split(/\s+/u).filter(Boolean);
    const isOrganization = !!root.closest('page-organization-list, page-organization-detail')
      || !!root.querySelector('organization-preview');
    const reorderedTerm = isOrganization && words.length > 1
      ? [...words.slice(1), words[0]].join(' ')
      : null;

    return reorderedTerm && reorderedTerm !== searchTerm
      ? [searchTerm, reorderedTerm]
      : [searchTerm];
  }

  public ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => {
      this.observer = new MutationObserver((records) => {
        this.recordChangedText(records);
        this.scheduleUpdate();
      });
    });

    this.querySubscription = this.route.queryParamMap.subscribe((params) => {
      this.searchTerm = (params.get(MULTI_FIELD_SEARCH_KEY) || '').trim();
      this.scheduleUpdate();
    });
  }

  public ngOnDestroy(): void {
    this.destroyed = true;
    this.querySubscription?.unsubscribe();
    this.recordChangedText(this.observer?.takeRecords() || []);
    this.observer?.disconnect();
    this.restoreText();
  }

  private scheduleUpdate(): void {
    if (this.scheduled || this.destroyed) {
      return;
    }

    this.scheduled = true;
    this.zone.runOutsideAngular(() => {
      queueMicrotask(() => {
        this.scheduled = false;

        if (!this.destroyed) {
          this.updateHighlights();
        }
      });
    });
  }

  private updateHighlights(): void {
    this.recordChangedText(this.observer?.takeRecords() || []);
    this.observer?.disconnect();
    this.restoreText();

    const root = this.host.nativeElement as HTMLElement;
    const { searchTerm } = this;

    if (!searchTerm || root.classList.contains('modal-content') || root.closest('page-classification-list')) {
      return;
    }

    const isDetail = root.classList.contains('detail-content');
    const contexts = isDetail
      ? Array.from(root.querySelectorAll<HTMLElement>('.proto-content__detail'))
      : Array.from(root.querySelectorAll<HTMLElement>('.item-label'));
    const excludedSelector = isDetail
      ? [...EXCLUDED_ELEMENTS, ...DETAIL_CONTROLS].join(', ')
      : EXCLUDED_ELEMENTS.join(', ');
    const terms = SearchHighlightDirective.getHighlightTerms(searchTerm, root);

    for (const context of contexts) {
      this.markContext(context, terms, excludedSelector, isDetail);
    }

    this.observeContent();
  }

  private recordChangedText(records: MutationRecord[]): void {
    for (const record of records) {
      if (record.type === 'characterData') {
        this.changedText.add(record.target as Text);
      }
    }
  }

  private markContext(context: HTMLElement, terms: string[], excludedSelector: string, isDetail: boolean): void {
    const walker = document.createTreeWalker(context, NodeFilter.SHOW_TEXT);
    const nodes: Text[] = [];
    let node = walker.nextNode() as Text | null;

    while (node) {
      if (node.data && SearchHighlightDirective.isVisible(node, excludedSelector)) {
        nodes.push(node);
      }

      node = walker.nextNode() as Text | null;
    }

    if (isDetail) {
      this.markNodes(nodes, terms);
    } else {
      for (const text of nodes) {
        this.markNodes([text], terms);
      }
    }
  }

  private markNodes(nodes: Text[], terms: string[]): void {
    const content = nodes.map((node) => { return node.data; }).join('');
    const matches = SearchHighlightDirective.findMatches(content, terms);
    let offset = 0;
    let matchIndex = 0;

    for (const node of nodes) {
      const end = offset + node.length;
      const pieces: Match[] = [];

      while (matchIndex < matches.length && matches[matchIndex].start < end) {
        const match = matches[matchIndex];

        pieces.push({
          end: Math.min(node.length, match.end - offset),
          start: Math.max(0, match.start - offset),
        });

        if (match.end > end) {
          break;
        }

        matchIndex += 1;
      }

      if (pieces.length) {
        this.markText(node, pieces);
      }

      offset = end;
    }
  }

  private markText(original: Text, matches: Match[]): void {
    const source = original.data;
    const inserted: Node[] = [];
    let offset = 0;

    for (const match of matches) {
      if (match.start > offset) {
        inserted.push(document.createTextNode(source.slice(offset, match.start)));
      }

      const mark = document.createElement('mark');

      mark.className = HIGHLIGHT_CLASS;
      mark.textContent = source.slice(match.start, match.end);
      inserted.push(mark);
      offset = match.end;
    }

    if (offset < source.length) {
      inserted.push(document.createTextNode(source.slice(offset)));
    }

    const fragment = document.createDocumentFragment();

    fragment.append(...inserted);
    // Keep Angular's original text node in place so later bindings can still update it.
    original.parentNode?.insertBefore(fragment, original.nextSibling);
    original.data = '';
    this.marked.push({
      inserted,
      original,
    });
  }

  private restoreText(): void {
    for (const { original, inserted } of this.marked) {
      // An Angular update to the original node takes precedence over the old highlighted copy.
      if (!this.changedText.has(original)) {
        original.data = inserted.map((node) => { return node.textContent || ''; }).join('');
      }

      for (const node of inserted) {
        node.parentNode?.removeChild(node);
      }
    }

    this.marked = [];
    this.changedText.clear();
  }

  private observeContent(): void {
    if (this.destroyed) {
      return;
    }

    this.observer?.observe(this.host.nativeElement, {
      attributeFilter: ['class', 'hidden', 'aria-hidden'],
      attributes: true,
      characterData: true,
      childList: true,
      subtree: true,
    });
  }
}
